import React from "react";
import { useCallback } from "react";
import { Box } from "@mui/material";

import Typography from "@mui/material/Typography";
import { Tooltip } from "@mui/material";
import { Skeleton } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";

import { LinearProgress } from "@mui/material";
import { Button } from "@mui/material";

import LearnTextEmbed from "../LearnPanelComponents/LearnCopy/LearnTextEmbed";

import { useTheme } from "@mui/material/styles";
import MyCountUp from "../UtilityComponents/NumberDisplayComponents/MyCountUp";
import { isMobile } from "react-device-detect";

const enterToolTipDelay = 400;

type DetailsStatsProps = {
  noStockPlans: boolean;
  statsError: boolean;
  totalStockValue: number;
  lastDrawnTotalStockValue: number | undefined;
  setLastDrawnTotalStockValue: (value: number) => void;
  vestedValue: number;
  lastDrawnVestedValue: number | undefined;
  setLastDrawnVestedValue: (value: number) => void;
  pctVested: number;
  lastDrawnPctVested: number | undefined;
  setLastDrawnPctVested: (value: number) => void;
  sharesSold: number;
  lastDrawnSharesSold: number | undefined;
  setLastDrawnSharesSold: (value: number) => void;
  daysUntilNextVest: number | null;
  lastDrawnDaysUntilNextVest: number | undefined;
  setLastDrawnDaysUntilNextVest: (value: number) => void;
  loading: boolean;
  userSymbol: string;
  referencePrice: number;
  lastDrawnReferencePrice: number | undefined;
  setLastDrawnReferencePrice: (value: number) => void;
  firstCheckDone: boolean;
  startAdd: () => void;
  loadingAddStockPlan: boolean;
};

function DetailsStats(props: DetailsStatsProps) {
  const theme = useTheme();

  if (!props.firstCheckDone) {
    return (
      <>
        <Typography variant="h6">
          <Skeleton width="25%" />
        </Typography>
        <Typography variant="h4" width="50%">
          <Skeleton />
        </Typography>
      </>
    );
  }

  if (props.noStockPlans) {
    return (
      <Box
        sx={{
          flexGrow: 0,
          sizing: "border-box",
          maxWidth: "1250px",
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} color="info.main">
          Break down your equity-based compensation details (4 min)
        </Typography>
        <Typography variant={isMobile ? "h6" : "h5"} color="text.secondary">
          <LearnTextEmbed content="Learn about how your equity-based compensation is structured and vests over time." />
          <br />
          Click the{" "}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            color="primary"
            onClick={() => {
              if (props.startAdd !== undefined && !props.loadingAddStockPlan) {
                props.startAdd();
              }
            }}
          >
            Add a Stock Plan
          </Button>{" "}
          <LearnTextEmbed
            content="button to tell us a bit about your stock plans. You can often find this information in your offer letter,
          annual compensation letter, or company brokerage portal (AI-automated upload coming soon!). If you have any
          questions or trouble finding this information, please reach out to us at contact@usesalus.com for help!"
          />
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="highlight-for-overview-tour-step-1"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: isMobile ? undefined : "space-evenly",
        sizing: "border-box",
        flexGrow: 0,
        flexWrap: "wrap",
      }}
    >
      {[
        {
          description: "Percent Vested",
          value: (
            <Box alignItems="center" sx={{ display: "flex", flexDirection: "row", width: 150 }}>
              <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={props.pctVested * 100}
                  sx={{
                    width: "100%",
                    height: 30,
                    border: 0.5,
                    borderRadius: 1,
                    borderColor: props.pctVested > 0.99 ? theme.palette.success.light : theme.palette.info.light,
                    backgroundColor: theme.palette.background.default,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: props.pctVested > 0.99 ? theme.palette.success.main : theme.palette.info.main,
                    },
                  }}
                />
              </Box>
              <MyCountUp
                value={props.pctVested * 100}
                lastFullyDrawnValue={props.lastDrawnPctVested}
                setLastFullyDrawnValue={props.setLastDrawnPctVested}
                suffix="%"
                negativeIsError
                error={props.statsError}
              />
            </Box>
          ),
          tooltip_text: "The percentage of your shares that have vested.",
        },
        {
          description: "Vested Stock Held",
          value: (
            <MyCountUp
              value={props.vestedValue}
              lastFullyDrawnValue={props.lastDrawnVestedValue}
              setLastFullyDrawnValue={props.setLastDrawnVestedValue}
              prefix="$"
              negativeIsError
              error={props.statsError}
            />
          ),
          tooltip_text:
            "This is the value of your current stock position, excluding any shares that have not vested yet. This is the value of your unsold, vested shares at the current stock price.",
        },
        {
          description: "Vested Stock Sold",
          value: (
            <MyCountUp
              value={props.sharesSold}
              lastFullyDrawnValue={props.lastDrawnSharesSold}
              setLastFullyDrawnValue={props.setLastDrawnSharesSold}
              suffix={props.lastDrawnSharesSold === 1 && props.sharesSold === 1 ? " share" : " shares"}
              negativeIsError
              error={props.statsError}
            />
          ),
          tooltip_text: "This is the total number of vested shares that you have sold.",
        },
        {
          description: "Next Vesting in",
          value:
            (props.daysUntilNextVest === null || props.daysUntilNextVest < 0) && !props.statsError ? (
              "∞ days"
            ) : (
              <MyCountUp
                value={props.daysUntilNextVest ?? -1}
                lastFullyDrawnValue={props.lastDrawnDaysUntilNextVest ?? 365}
                setLastFullyDrawnValue={props.setLastDrawnDaysUntilNextVest}
                suffix={props.daysUntilNextVest === 1 && props.lastDrawnDaysUntilNextVest === 1 ? " day" : " days"}
                negativeIsError
                error={props.statsError}
              />
            ),
          tooltip_text: "This is how many days until your next vesting tranche.",
        },
        {
          description: "Total Stock Value",
          value: (
            <MyCountUp
              value={props.totalStockValue}
              lastFullyDrawnValue={props.lastDrawnTotalStockValue}
              setLastFullyDrawnValue={props.setLastDrawnTotalStockValue}
              prefix="$"
              negativeIsError
              error={props.statsError}
            />
          ),
          tooltip_text:
            "This is the total stock value of all grants with current market conditions after accounting for any sold shares.",
        },
        {
          description: (props.userSymbol ? props.userSymbol + " " : "") + "Stock Price",
          value: (
            <MyCountUp
              value={props.referencePrice}
              lastFullyDrawnValue={props.lastDrawnReferencePrice}
              setLastFullyDrawnValue={props.setLastDrawnReferencePrice}
              prefix="$"
              decimals={2}
              negativeIsError
            />
          ),
          tooltip_text: "This is the last close price for the referenced stock in the stock plans.",
        },
      ].map((label, index) => (
        <Tooltip key={index} title={label.tooltip_text} placement="bottom-start" arrow enterDelay={enterToolTipDelay}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              sizing: "border-box",
              px: 1,
              flex: {
                sm: "0 0 50%",
                md: "none",
              },
            }}
          >
            <Typography variant="h6" noWrap sx={{ color: "info.main" }}>
              {label.description}
            </Typography>
            <Typography variant="h4" noWrap>
              {props.loading ? (
                // TODO: lift this state so that if it changes dynamically it'll look nice as it goes to that new number
                <Skeleton />
              ) : (
                label.value
              )}
            </Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

export default DetailsStats;
